/**
 * Coupon Panel Auto-Expand
 *
 * Automatically expands the coupon panel on PC to show the input and button.
 * On mobile, keeps the toggle behavior.
 *
 * This is needed because WooCommerce Blocks doesn't render the coupon form
 * content when the panel is collapsed, so CSS alone cannot force it visible.
 *
 * v1.16.23 - Fix: mantener panel abierto después de aplicar cupón
 *            - MutationObserver persistente (no se desconecta)
 *            - Respeta spcbCheckoutLock para no interferir durante procesamiento
 *
 * @package SuperPymeCheckoutBlocks
 * @version 1.16.23
 */

(function() {
    'use strict';

    const DEBUG = window.location.search.includes('spcb_debug=1');
    const PC_BREAKPOINT = 783; // Match CSS media query
    const DEBOUNCE_MS = 150; // Evita múltiples clicks seguidos

    let lastExpandAttempt = 0;

    const log = (...args) => {
        if (DEBUG) {
            console.log('[SPCB Coupon]', ...args);
        }
    };

    /**
     * Check if we're on PC (wide screen)
     */
    function isPC() {
        return window.innerWidth >= PC_BREAKPOINT;
    }

    /**
     * CRÍTICO: Verificar si el checkout está procesando
     * Patrón estándar del plugin - ver CLAUDE.md sección 19.1
     */
    function isCheckoutProcessing() {
        return window.spcbCheckoutLock?.isProcessing?.() === true;
    }

    /**
     * Change the coupon input placeholder text
     */
    function updateCouponPlaceholder() {
        const couponLabel = document.querySelector('.wc-block-components-totals-coupon__input label');
        if (couponLabel && couponLabel.textContent !== 'Código de descuento') {
            couponLabel.textContent = 'Código de descuento';
            log('Coupon placeholder updated');
        }
    }

    /**
     * Expand the coupon panel by clicking the toggle button
     * @returns {boolean} true si se expandió o ya estaba expandido
     */
    function expandCouponPanel() {
        // GUARD 1: No ejecutar en móvil
        if (!isPC()) {
            log('Not PC, skipping');
            return false;
        }

        // GUARD 2: CRÍTICO - No ejecutar durante procesamiento del checkout
        if (isCheckoutProcessing()) {
            log('Checkout processing - blocked');
            return false;
        }

        // Find the coupon panel toggle button
        const couponToggle = document.querySelector('.wc-block-components-totals-coupon .wc-block-components-panel__button');

        if (!couponToggle) {
            log('Toggle not found');
            return false;
        }

        // Check if already expanded
        const isExpanded = couponToggle.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
            log('Panel already expanded');
            updateCouponPlaceholder();
            return true;
        }

        // GUARD 3: Debounce - evita múltiples clicks en rápida sucesión
        const now = Date.now();
        if (now - lastExpandAttempt < DEBOUNCE_MS) {
            log('Debounced');
            return false;
        }
        lastExpandAttempt = now;

        // Click en el siguiente frame para evitar flickering
        requestAnimationFrame(() => {
            // Re-verificar TODOS los guards después del frame
            if (isCheckoutProcessing()) {
                log('Checkout started processing - aborting expand');
                return;
            }

            const toggle = document.querySelector('.wc-block-components-totals-coupon .wc-block-components-panel__button');
            if (toggle && toggle.getAttribute('aria-expanded') === 'false') {
                toggle.click();
                log('Panel expanded');
                setTimeout(updateCouponPlaceholder, 50);
            }
        });

        return true;
    }

    /**
     * Initialize coupon auto-expand
     */
    function init() {
        log('Initializing coupon auto-expand v1.16.23');

        // Intento inicial
        expandCouponPanel();

        // Observer PERSISTENTE - NO se desconecta
        // Esto permite detectar cuando WC Blocks re-renderiza después de aplicar/eliminar cupón
        const observer = new MutationObserver((mutations) => {
            // GUARD: No procesar durante checkout
            if (isCheckoutProcessing()) return;
            if (!isPC()) return;

            // Buscar cambios relevantes en el área de cupones
            for (const mutation of mutations) {
                const isInCouponArea = mutation.target.closest?.('.wc-block-components-totals-coupon');
                if (isInCouponArea) {
                    expandCouponPanel();
                    break; // Un intento por batch de mutations
                }
            }
        });

        // Observar solo el contenedor de checkout (más eficiente que body)
        const checkoutContainer = document.querySelector('.wc-block-checkout') || document.body;

        observer.observe(checkoutContainer, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['aria-expanded', 'class']
        });

        log('Persistent observer started');
    }

    // Inicializar cuando DOM esté listo
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => setTimeout(init, 300));
    } else {
        // DOM already loaded, wait for React render
        setTimeout(init, 300);
    }

    // Re-check en resize (con guards)
    let resizeTimeout;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            if (isPC() && !isCheckoutProcessing()) {
                expandCouponPanel();
            }
        }, 300);
    });
})();
